/**
 * Utility function to translate requirement terms from CSV data
 * This handles the translation of hierarchy terms like "Architecture", "BIM/CAD", etc.
 */

/**
 * Translates a requirement term using the translation function
 * @param {string} term - The original English term from CSV
 * @param {function} t - The translation function from i18n
 * @returns {string} - The translated term or original if no translation exists
 */
export const translateRequirementTerm = (term, t) => {
  if (!term || typeof term !== 'string') {
    return term;
  }

  // Try to find a translation for this term
  const translationKey = `requirements.terms.${term}`;
  const translated = t(translationKey, term);
  
  // If the translation is the same as the key, it means no translation was found
  // In that case, return the original term
  return translated !== translationKey ? translated : term;
};

/**
 * Translates all hierarchy fields in a requirement
 * @param {object} hierarchy - The hierarchy object with mainCategory, subCategory, etc.
 * @param {function} t - The translation function from i18n
 * @returns {object} - The hierarchy object with translated terms
 */
export const translateHierarchy = (hierarchy, t) => {
  if (!hierarchy || typeof hierarchy !== 'object') {
    return hierarchy;
  }

  const translatedHierarchy = {};
  
  Object.keys(hierarchy).forEach(key => {
    const value = hierarchy[key];
    if (typeof value === 'string' && value.trim()) {
      translatedHierarchy[key] = translateRequirementTerm(value, t);
    } else {
      translatedHierarchy[key] = value;
    }
  });

  return translatedHierarchy;
};

/**
 * Gets the translated title from a hierarchy object
 * @param {object} hierarchy - The hierarchy object
 * @param {function} t - The translation function from i18n
 * @returns {string} - The translated title
 */
export const getTranslatedTitleFromHierarchy = (hierarchy = {}, t) => {
  const translatedHierarchy = translateHierarchy(hierarchy, t);
  
  return (
    translatedHierarchy.level7 ||
    translatedHierarchy.level6 ||
    translatedHierarchy.level5 ||
    translatedHierarchy.level4 ||
    translatedHierarchy.subSubCategory ||
    translatedHierarchy.subCategory ||
    translatedHierarchy.mainCategory ||
    t('requirements.untitled', 'Untitled')
  );
};
